import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { Exclude, Expose } from "class-transformer";
import { IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanPatientMonitor extends FloorplanProductItem<PatientMonitorConfiguration> {
  @Exclude()
  private readonly _patientMonitor: PatientMonitorConfiguration;

  @IsString()
  @Expose({ name: "patientMonitorId" })
  private readonly _patientMonitorId: string;

  constructor(
    floorplan: Floorplan,
    patientMonitorConfiguration: PatientMonitorConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    notes?: string,
  ) {
    super(floorplan, patientMonitorConfiguration, id, x, y, size, notes);
    this._patientMonitorId = patientMonitorConfiguration?.id;
    this._patientMonitor = patientMonitorConfiguration;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.patientMonitorsItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanPatientMonitor) | undefined {
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => FloorplanPatientMonitor.create(this._floorplan, config, x, y, size);
  }

  override delete() {
    this._floorplan.deletePatientMonitor(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.patientMonitors;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanPatientMonitor,
        this._floorplan.patientMonitors,
        FloorplanEventType.PATIENT_MONITOR_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanPatientMonitor) => void) | undefined,
      );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshPatientMonitorsLock();
  }

  protected override getConfigFromProject(floorplan: Floorplan): PatientMonitorConfiguration | undefined {
    return floorplan.getPatientMonitorConfig(this._patientMonitorId);
  }

  static create(
    floorplan: Floorplan,
    patientMonitor: PatientMonitorConfiguration,
    x: number,
    y: number,
    size: number,
  ): FloorplanPatientMonitor {
    return new FloorplanPatientMonitor(floorplan, patientMonitor, uuidv4(), x, y, size);
  }
}
